/**
 * Keyboard Shortcuts Hook - Enhanced with number keys and page-specific actions
 */
import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useUIStore } from '../stores/uiStore'

interface ShortcutConfig {
  onCommandPalette?: () => void
  onEscape?: () => void
  onAddTicker?: () => void // W key on watchlist
  onCreateAlert?: () => void // A key on alerts
  onAddPosition?: () => void // P key on portfolio
  onRefresh?: () => void // R key anywhere
  onSave?: () => void // Ctrl+S for strategy
  onToggleSidebar?: () => void // [ key
  onToggleAIPanel?: () => void // Cmd+. key
}

// Track if command palette or modal is open
let commandPaletteOpen = false
export const setCommandPaletteOpen = (open: boolean) => {
  commandPaletteOpen = open
}

export function useKeyboardShortcuts(config: ShortcutConfig = {}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toggleSidebar, toggleAIPanel } = useUIStore()

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const target = e.target as HTMLElement
    const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable

    // Command palette: Ctrl/Cmd + K - always works
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault()
      config.onCommandPalette?.()
      window.dispatchEvent(new CustomEvent('open-command-palette'))
      return
    }

    // AI Panel: Ctrl/Cmd + . - toggle AI panel
    if ((e.ctrlKey || e.metaKey) && e.key === '.') {
      e.preventDefault()
      if (config.onToggleAIPanel) {
        config.onToggleAIPanel()
      } else {
        toggleAIPanel()
      }
      return
    }

    // Settings: Ctrl/Cmd + , - go to settings
    if ((e.ctrlKey || e.metaKey) && e.key === ',') {
      e.preventDefault()
      navigate('/settings')
      return
    }

    // Save: Ctrl/Cmd + S - for strategy editor
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault()
      config.onSave?.()
      return
    }

    // Escape key - always works
    if (e.key === 'Escape') {
      config.onEscape?.()
      window.dispatchEvent(new CustomEvent('close-modal'))
      return
    }

    // Don't process other shortcuts if in input or modal is open
    if (isInput || commandPaletteOpen) return

    // Sidebar toggle: [ key
    if (e.key === '[') {
      e.preventDefault()
      if (config.onToggleSidebar) {
        config.onToggleSidebar()
      } else {
        toggleSidebar()
      }
      return
    }

    // Number keys for quick navigation: 1-4
    if (e.key === '1') {
      e.preventDefault()
      navigate('/dashboard')
      return
    }
    if (e.key === '2') {
      e.preventDefault()
      navigate('/watchlist')
      return
    }
    if (e.key === '3') {
      e.preventDefault()
      navigate('/portfolio')
      return
    }
    if (e.key === '4') {
      e.preventDefault()
      navigate('/alerts')
      return
    }

    // R: Refresh data
    if (e.key === 'r' || e.key === 'R') {
      e.preventDefault()
      if (config.onRefresh) {
        config.onRefresh()
      } else {
        queryClient.invalidateQueries()
      }
      return
    }

    // W: Add ticker (watchlist page)
    if (e.key === 'w' || e.key === 'W') {
      if (config.onAddTicker) {
        e.preventDefault()
        config.onAddTicker()
      }
      return
    }

    // A: Create alert (alerts page)
    if (e.key === 'a' || e.key === 'A') {
      if (config.onCreateAlert) {
        e.preventDefault()
        config.onCreateAlert()
      }
      return
    }

    // P: Add position (portfolio page)
    if (e.key === 'p' || e.key === 'P') {
      if (config.onAddPosition) {
        e.preventDefault()
        config.onAddPosition()
      }
      return
    }

    // /: Focus search input
    if (e.key === '/') {
      const searchInput = document.querySelector<HTMLInputElement>(
        '[data-search-input], input[type="search"], .search-input input, .command-input'
      )
      if (searchInput) {
        e.preventDefault()
        searchInput.focus()
      }
      return
    }

    // ? to show shortcuts help
    if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      window.dispatchEvent(new CustomEvent('apex-show-shortcuts'))
      return
    }

    // Navigation shortcuts (g + key pattern) - vim-style go-to navigation
    if (e.key === 'g') {
      const handleNextKey = (nextE: KeyboardEvent) => {
        document.removeEventListener('keydown', handleNextKey)
        nextE.preventDefault()

        switch (nextE.key) {
          case 'h':
          case 'd':
            navigate('/')
            break
          case 'w':
            navigate('/watchlist')
            break
          case 'r':
            navigate('/stock')
            break
          case 's':
            navigate('/screener')
            break
          case 'o':
            navigate('/options')
            break
          case 'm':
            navigate('/macro')
            break
          case 'p':
            navigate('/portfolio')
            break
          case 'a':
            navigate('/alerts')
            break
          case 't':
            navigate('/strategy-studio')
            break
        }
      }

      document.addEventListener('keydown', handleNextKey, { once: true })
      setTimeout(() => {
        document.removeEventListener('keydown', handleNextKey)
      }, 1000)
    }
  }, [navigate, config, queryClient, toggleSidebar, toggleAIPanel])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}

// Hook to set page title
export function usePageTitle(title: string) {
  useEffect(() => {
    const formatted = title ? `${title} — Cipher` : 'Cipher'
    document.title = formatted
    return () => { document.title = 'Cipher' }
  }, [title])
}
